import type WaButton from "@web.awesome.me/webawesome-pro/dist/components/button/button.js";
import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import htmx from "htmx.org";
import { css, html } from "lit";
import { checkNotNil } from "../../check-not-nil";
import { type FormSubmissionHandler, formSubmissionHandler } from "../../form";
import { ValidationError } from "../../schemas/validation-error.js";
import { hostStyles } from "../../styles/host.styles";
import type { WmChecklist } from "../checklist/checklist";
import { WmElement } from "../element.js";

export class WmTaskCreate extends WmElement {
  static readonly TAG_NAME = "wm-task-create";

  static styles = [
    hostStyles,
    // TODO: form style?
    css`
      form {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: var(--wa-space-m);
      }

      wa-button {
        align-self: start;
      }
    `,
  ];

  #formSubmit: FormSubmissionHandler;

  constructor() {
    super();
    this.#formSubmit = formSubmissionHandler("/app/tasks", {
      method: "POST",
    })
      .on("pre:response", () => (this.#querySubmitButton().loading = true))
      .on("response", (r) => this.#handleResponse(r))
      .on("pre:response", () => (this.#querySubmitButton().loading = false))
      .on("error", (err) => {
        this.logger.error("unable to POST /app/tasks", err);
      });
  }

  protected override render() {
    // Passing the form submission as an object rather than the function
    // directly. This is because the function has a "once" field that Lit
    // misinterprets to mean that we want the listener to fire only once.
    // This is problematic if the first call results in 422, the user fixes
    // the error, then resubmits.
    return html`
      <form @submit=${{ handleEvent: this.#formSubmit }}>
        <wa-input
          label="Description"
          name="description"
          required
          @input=${this.#handleDescriptionInput}>
        </wa-input>
        <wm-datetime-input name="due_by" label="Due by">
        </wm-datetime-input>
        <wm-checklist name="checklist" label="Checklist">
        </wm-checklist>
        <wm-tags-combobox name="tags">
        </wm-tags-combobox>
        <wa-button variant="success" type="submit">Create</wa-button>
      </form>
    `;
  }

  async #handleResponse(response: Response) {
    // Handle 422 with JSON serialized error messages.
    if (response.status === 422) {
      const validationError = ValidationError.parse(await response.json());
      const { path, reason } = validationError.error;

      const checklistPathRegex = /checklist\[(\d+)\].description/;

      if (path === "description") {
        const descriptionInput = this.#queryDescriptionInput();
        descriptionInput.setCustomValidity(reason);
        descriptionInput.reportValidity();
      } else if (checklistPathRegex.test(path)) {
        const captures = checkNotNil(checklistPathRegex.exec(path));
        const indexString = captures[1];
        const index = parseInt(indexString, 10);
        const checklist = this.#queryChecklist();
        checklist.reportError(index, reason);
      } else {
        this.logger.warn(
          "unknown invalid field from 422 response: %s",
          path,
          validationError,
        );
      }
    } else {
      return htmx.ajax("GET", "/app/tasks", { push: "true" });
    }
  }

  #handleDescriptionInput(e: InputEvent) {
    const input = checkNotNil(e.target) as WaInput;
    input.setCustomValidity("");
  }

  #queryChecklist(): WmChecklist {
    return this.queryNotNil("wm-checklist");
  }

  #queryDescriptionInput(): WaInput {
    return this.queryNotNil("wa-input");
  }

  #querySubmitButton(): WaButton {
    return this.queryNotNil(`wa-button[type="submit"]`);
  }
}

customElements.define(WmTaskCreate.TAG_NAME, WmTaskCreate);

declare global {
  interface HTMLElementTagNameMap {
    [WmTaskCreate.TAG_NAME]: WmTaskCreate;
  }
}
